import { Injectable } from '@angular/core';
import { Observable, finalize, mergeMap, from as observableFrom } from 'rxjs';
import { Command, CommandBase, FilteredCommand, MultiselectCommand } from '../../../core/model/command';
import { ICommandHandler } from '../../../core/service/command/command-handler-resolver.service';
import { ComponentMessageDispatcher } from '../../../core/service/component-message-dispather.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { LeaseAgreementFilteredCommand } from '../../../rent/command/lease-agreement.command';
import { Contract } from '../../../rent/model/contract';
import { Tenant } from '../../../rent/model/tenant';
import { IEmailReceiver } from '../../../rent/service/base/contract';
import { BlockContractService } from '../../../rent/service/data/block-contract.service';
import { CooperativeHousingContractService } from '../../../rent/service/data/cooperative-housing-contract.service';
import { DwellingContractService } from '../../../rent/service/data/dwelling-contract.service';
import { OtherContractService } from '../../../rent/service/data/other-contract.service';
import { ParkingContractService } from '../../../rent/service/data/parking-contract.service';
import { PremisesContractService } from '../../../rent/service/data/premises-contract.service';
import { LeaseAgreementService } from '../../../rent/service/data/lease-agreement.service';
import { mailCommand } from '../../model/const/command';
import { LeaseAgreementCommandHandlerBase } from './lease-agreement-command-handler.base';

@Injectable()
export class SendTenantEmailCommandHandler extends LeaseAgreementCommandHandlerBase implements ICommandHandler {
  constructor(
    private readonly _messageDispatcher: ComponentMessageDispatcher,
    private readonly _navigation: NavigationService,
    private readonly _progressBar: ProgressBarService,
    dwellingService: DwellingContractService,
    parkingService: ParkingContractService,
    premisesService: PremisesContractService,
    otherService: OtherContractService,
    blockService: BlockContractService,
    cooperativeService: CooperativeHousingContractService,
    private leaseAgreementService: LeaseAgreementService
  ) {
    super(dwellingService, parkingService, premisesService, otherService, blockService, cooperativeService);
  }

  canExecute(command: CommandBase): boolean {
    return command.id == mailCommand.sendTenantEmail;
  }

  execute(command: CommandBase): Observable<boolean> {
    let tenants: IEmailReceiver[] = [];
    if (command instanceof MultiselectCommand) {
      tenants = command.items.filter(x => x.emailReceivers.length > 0);
    } else if (command instanceof FilteredCommand) {
      try {
        this._progressBar.start();

        const categoryCommand = command as LeaseAgreementFilteredCommand;
        var useLeaseAgreementController = categoryCommand.filterProvider.filterState.sendMessageType !== undefined;

        if (useLeaseAgreementController) {
          return this.leaseAgreementService
            .getEmailFiltered(
              command.filterProvider.filterState,
              command.includedIds,
              command.excludedIds,
              command.totalAffectedItemsControlValue
            )
            .pipe(
              mergeMap(tenants => this.dispatchAndNavigate(tenants)),
              finalize(() => this._progressBar.stop())
            );
        } else {
          const service = this._getService(categoryCommand.category);
          return service
            .getEmailFiltered(
              command.filterProvider.filterState,
              command.includedIds,
              command.excludedIds,
              command.totalAffectedItemsControlValue
            )
            .pipe(
              mergeMap(tenants => this.dispatchAndNavigate(tenants)),
              finalize(() => this._progressBar.stop())
            );
        }
      } catch {
        this._progressBar.stop();
      }
    } else if (command instanceof Command) {
      const contract = <Contract>command.context;
      tenants.push({
        id: contract.id, // TODO: "emailReceivers" below is not 100% correct, but the old code was even worse... fix it better another day. --Nick
        emailReceivers: (contract.tenantOne instanceof Tenant ? contract.tenantOne.email : contract.tenantOneEmail) || '',
      });
      if (contract && typeof contract.tenantTwoEmail === 'string' && contract.tenantTwoEmail.trim().length > 0)
        tenants.push({
          id: contract.id,
          emailReceivers: contract.tenantTwoEmail,
        });
    }
    return this.dispatchAndNavigate(tenants);
  }

  private dispatchAndNavigate(tenants: IEmailReceiver[]) {
    if (tenants.length > 0) {
      this._messageDispatcher.send({ tenants: tenants.map(item => ({ id: item.id, recipientName: item.emailReceivers })) });
      return observableFrom(this._navigation.navigate(['email', 'sendemail']));
    }
  }
}
